export const PERSON_NODE_SIZE_NORMAL = 54;
export const PERSON_NODE_SIZE_ROOT = 96;
export const PERSON_NODE_SIZE_MIN = 34;
/** Extra diameter applied on hover above the standard avatar size. */
export const PERSON_NODE_HOVER_BOOST = 10;
/** Band label chip width in graph units (must match Cytoscape style). */
export const BAND_LABEL_WIDTH = 112;
/** Horizontal span used when reserving space around the chip (slightly tighter than visual width). */
const BAND_LABEL_RESERVATION_WIDTH = 60;
/** Extra space kept between avatars and the band label at the top of each ring. */
export const BAND_LABEL_CLEARANCE = 0;
/** Minimum arc gap between adjacent avatars on the same ring. */
const ORBIT_NODE_GAP = 10;

/** Top of the orbit where level chips sit (12 o'clock). */
export const BAND_LABEL_ANGLE = -Math.PI / 2;

function computeReservedTopHalfAngle(
  bandRadius: number,
  maxNodeSize: number,
): number {
  const halfOccupied =
    BAND_LABEL_RESERVATION_WIDTH / 2 + maxNodeSize / 2 + BAND_LABEL_CLEARANCE;
  return Math.asin(Math.min(1, halfOccupied / bandRadius));
}

function computeUsableOrbitArc(bandRadius: number, maxNodeSize: number): number {
  const reservedHalfAngle = computeReservedTopHalfAngle(bandRadius, maxNodeSize);
  return 2 * Math.PI - 2 * reservedHalfAngle;
}

/**
 * Fits avatar diameter to the ring arc length so crowded orbits shrink icons
 * instead of overlapping. Reserves the top of each ring for the level chip.
 */
export function computeOrbitNodeSize(
  bandRadius: number,
  nodeCount: number,
): number {
  if (nodeCount <= 0) {
    return PERSON_NODE_SIZE_NORMAL;
  }

  const maxNodeSize = PERSON_NODE_SIZE_NORMAL;
  const usableArc = computeUsableOrbitArc(bandRadius, maxNodeSize);
  const arcLength = (bandRadius * usableArc) / nodeCount;
  const fitSize = arcLength - ORBIT_NODE_GAP;
  return Math.max(
    PERSON_NODE_SIZE_MIN,
    Math.min(PERSON_NODE_SIZE_NORMAL, fitSize),
  );
}

/** Hover size is always visibly larger than the resting avatar. */
export function computeHoverNodeSize(
  compactSize: number,
  normalSize: number,
): number {
  return Math.max(normalSize + PERSON_NODE_HOVER_BOOST, compactSize + 12);
}

export function isOrbitOvercrowded(
  bandRadius: number,
  nodeCount: number,
): boolean {
  return (
    nodeCount > 0 &&
    computeOrbitNodeSize(bandRadius, nodeCount) < PERSON_NODE_SIZE_NORMAL
  );
}

/** Evenly distributes nodes on a ring while keeping the top spoke clear for the chip. */
export function computeOrbitAngles(
  bandRadius: number,
  nodeCount: number,
  maxNodeSize: number,
): number[] {
  if (nodeCount <= 0) {
    return [];
  }

  const reservedHalfAngle = computeReservedTopHalfAngle(bandRadius, maxNodeSize);
  const usableArc = 2 * Math.PI - 2 * reservedHalfAngle;
  const arcStart = BAND_LABEL_ANGLE + reservedHalfAngle;
  const step = usableArc / nodeCount;

  return Array.from({ length: nodeCount }, (_, index) => {
    return arcStart + step * (index + 0.5);
  });
}
